import {StoppageCode, useAggregatedStoppageStatistics, usePlantStoppages} from '@domain/stoppages'
import {DataContentWrapper} from '@hconnect/common/containers'
import {StoppageKind} from '@hconnect/common/types'
import {formatNumber} from '@hconnect/common/utils'
import {CardBox} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {Box} from '@mui/material'
import {isEmpty, orderBy} from 'lodash'
import {FC, useCallback, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {usePlantTimeRange} from '../../hooks'
import {useDowntimePlantId} from '../../providers'
import {getBarTimeFormattedLabel, getTimeStep, periodTimeDataIsEmpty} from '../../utils'

import {ComboChart, ComboChartData} from './ComboChart'

type TopIssuesByCodeProps = {
  stoppageConfig?: StoppageCode[]
  selectedEquipments?: string[]
  selectedEquipmentNumbers?: string[]
  selectedStoppages?: StoppageKind[]
  selectedStoppageCodes?: string[]
  title: string
  issueCount: number
  hideInfoIcon?: boolean
  showFramesLabelFromTimeRange?: boolean
}

export const TopIssuesByCode: FC<TopIssuesByCodeProps> = ({
  stoppageConfig: StoppageConfigProp,
  selectedEquipments,
  selectedEquipmentNumbers,
  selectedStoppages,
  selectedStoppageCodes
}) => {
  const plantId = useDowntimePlantId()
  const {
    t,
    i18n: {language}
  } = useTranslation(FEATURE_NAMESPACE)
  const timezone = useUpmPlantTimezone(plantId)
  const timeRange = usePlantTimeRange()
  const [from, to] = [timeRange.from, timeRange.to]

  const timeStep = getTimeStep(timeRange, timezone)

  const timePeriodStatisticsQuery = useAggregatedStoppageStatistics(plantId, {
    from,
    to,
    mainEquipmentNumbers: selectedEquipments,
    equipmentNumbers: selectedEquipmentNumbers,
    kinds: selectedStoppages,
    stoppageCodes: selectedStoppageCodes,
    timeStep
  })
  const stoppageConfigQuery = usePlantStoppages(plantId, {enabled: !StoppageConfigProp})
  const stoppageConfig = StoppageConfigProp || stoppageConfigQuery.data

  const barLabel = t('label.hours')
  const lineLabel = t('label.noStoppages')

  const chartData = useMemo<ComboChartData<string> | undefined>(() => {
    if (timePeriodStatisticsQuery.data?.some((entry) => entry.stoppagesCount > 0)) {
      const sortedData = orderBy(timePeriodStatisticsQuery.data, 'timeStepStart')
      const barData: number[] = []
      const lineData: number[] = []
      sortedData.forEach((entry) => {
        const barValue = entry.hoursStopped
        const lineValue = Math.ceil(entry.stoppagesCount)
        barData.push(barValue)
        lineData.push(lineValue)
      })

      const columns: string[] = sortedData.map((entry) =>
        getBarTimeFormattedLabel({
          step: timeStep,
          dateUtc: entry.timeStepStart,
          language,
          timezone
        })
      )
      return {
        columns,
        barData,
        lineData,
        barLabel,
        lineLabel,
        dataTestId: 'top-issues-by-code-chart'
      }
    }
  }, [timePeriodStatisticsQuery.data, barLabel, lineLabel, timeStep, language, timezone])

  const retry = useCallback(() => {
    if (timePeriodStatisticsQuery.error) {
      void timePeriodStatisticsQuery.refetch()
    }
    if (stoppageConfigQuery.error) {
      void stoppageConfigQuery.refetch()
    }
  }, [timePeriodStatisticsQuery, stoppageConfigQuery])

  const getStoppageTitle = useCallback(
    (column) => {
      const stoppageCode = stoppageConfig?.find(({code}) => column.includes(code))
      return stoppageCode ? stoppageCode.code : ''
    },
    [stoppageConfig]
  )
  const getStoppageDescription = useCallback(
    (column) => {
      const stoppageCode = stoppageConfig?.find(({code}) => column.includes(code))
      return stoppageCode ? stoppageCode.text : ''
    },
    [stoppageConfig]
  )

  const getStoppageLabel = useCallback(
    (dataIndex: number, rawValue: number) => {
      if (dataIndex === 0) {
        return `${lineLabel}: ${formatNumber(rawValue, language)}`
      }
      return `${barLabel}: ${formatNumber(rawValue, language)}`
    },
    [barLabel, language, lineLabel]
  )

  return (
    <CardBox data-test-id="top-issues-by-code">
      <Box height={280}>
        <DataContentWrapper
          showLoader={
            timePeriodStatisticsQuery.isInitialLoading || stoppageConfigQuery.isInitialLoading
          }
          isNoData={
            periodTimeDataIsEmpty(timePeriodStatisticsQuery?.data) || isEmpty(stoppageConfig)
          }
          retryFunction={retry}
          isError={timePeriodStatisticsQuery.isError || stoppageConfigQuery.isError}
          isRefetching={timePeriodStatisticsQuery.isRefetching || stoppageConfigQuery.isRefetching}
          progressSx={{color: 'primary.main'}}
        >
          {chartData && (
            <ComboChart
              getTitle={getStoppageTitle}
              getAfterTitle={getStoppageDescription}
              getStoppageLabel={getStoppageLabel}
              data={chartData}
            />
          )}
        </DataContentWrapper>
      </Box>
    </CardBox>
  )
}
